import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HostelMapView from "@/components/HostelMapView";
import NearbyPlaces from "@/components/NearbyPlaces";
import HostelGallery from "@/components/HostelGallery";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CalendarDays, CalendarClock, MapPin, Star, Users, ShieldCheck, IndianRupee, Wifi, Utensils, BookOpen, ShowerHead, Snowflake, CheckCircle2, Phone, Share2, AlertCircle, LogOut } from "lucide-react";
import { format, differenceInDays } from "date-fns";
import ShareModal from "@/components/ShareModal";
import ScheduleVisitDialog from "@/components/ScheduleVisitDialog";

const AMENITY_ICONS = { WiFi: Wifi, Mess: Utensils, "Study Room": BookOpen, Laundry: ShowerHead, AC: Snowflake, CCTV: ShieldCheck, Library: BookOpen, Warden: ShieldCheck, Gym: CheckCircle2 };

export default function HostelDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [h, setH] = useState(null);
  const [checkIn, setCheckIn] = useState(new Date(Date.now() + 86400000));
  const [checkOut, setCheckOut] = useState(new Date(Date.now() + 8 * 86400000));
  const [beds, setBeds] = useState(1);
  const [roomType, setRoomType] = useState("Non-AC");
  const [selectedRoomTypeId, setSelectedRoomTypeId] = useState(null);
  const [includeMeal, setIncludeMeal] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState("full");   // "full" | "quarterly"
  const [loading, setLoading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [visitOpen, setVisitOpen] = useState(false);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get(`/hostels/${id}`).then((r) => setH(r.data)).catch(() => nav("/hostels"));
    api.get(`/hostels/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, [id, nav]);

  if (!h) return <div className="min-h-screen bg-[#FBF5EA]"><Navbar /><div className="p-20 text-center text-slate-500">Loading…</div></div>;

  const nights = Math.max(1, differenceInDays(checkOut, checkIn));
  const PLATFORM_FEE = 0; // zero platform fee — students pay exactly the listed price
  // If the student picked one of the owner's monthly room-type products,
  // the hostel subtotal is billed monthly ONLY for stays of 30+ nights;
  // short stays are billed pro-rata per night (price_per_month / 30 × nights)
  // so a 3-night stay never costs a full month.
  const selectedRoomType = (h.room_types || []).find((rt) => rt.id === selectedRoomTypeId) || null;
  const monthsCharged = nights >= 30 ? Math.ceil(nights / 30) : 0;
  const rtNightly = selectedRoomType ? Math.round(Number(selectedRoomType.price_per_month) / 30) : 0;
  const rate = roomType === "AC" && h.ac_available ? h.ac_price_per_bed : h.price_per_bed;
  const hostelSubtotal = selectedRoomType
    ? (monthsCharged ? Number(selectedRoomType.price_per_month) * monthsCharged : rtNightly * nights)
    : nights * rate * beds;
  const platformFee = nights * PLATFORM_FEE * beds;
  // If the picked room type bundles monthly food in the rent, meals are free — no daily food charges.
  const foodInRent = !!(selectedRoomType && selectedRoomType.food_included);
  const mealCost = includeMeal && h.meal_available && !foodInRent ? nights * h.meal_price * beds : 0;
  const total = hostelSubtotal + platformFee + mealCost;

  const isOwnerAccount = user && user.role === "owner";
  const isOwnHostel = user && h.owner_id === user.id;
  const needsKyc = user && user.role === "student" && user.kyc_status !== "verified";
  const cannotBookReason = !user
    ? null
    : isOwnHostel
    ? "This is your own hostel listing. You can't book your own beds."
    : user.role === "owner"
    ? "Your account is registered as a Property Owner. Bookings can only be made from a Student account."
    : needsKyc
    ? "Aadhaar KYC required — verify your identity before booking. Takes ~2 minutes."
    : null;
  const canBook = !!user && !cannotBookReason;

  const handleBook = async () => {
    if (!user) { toast.info("Please log in to book"); nav("/login?next=/hostels/" + id); return; }
    if (isOwnHostel) { toast.error("You can't book your own hostel listing."); return; }
    if (user.role === "owner") {
      toast.error("Owner accounts can't book beds. Create a student account to book.");
      return;
    }
    if (user.role !== "student" && user.role !== "admin") {
      toast.error("Only student accounts can book beds.");
      return;
    }
    if ((h.room_types || []).length > 0 && !selectedRoomTypeId) {
      toast.error("Please pick a room type before continuing.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        hostel_id: h.id,
        check_in: format(checkIn, "yyyy-MM-dd"),
        check_out: format(checkOut, "yyyy-MM-dd"),
        beds,
        room_type: roomType,
        include_meal: foodInRent ? false : includeMeal,
        payment_plan: paymentPlan,
        room_type_id: selectedRoomTypeId || undefined,
      });
      toast.success("Booking created. Redirecting to payment…");
      nav(`/booking/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FBF5EA]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
        {/* Gallery */}
        <HostelGallery
          images={h.images || []}
          priceBadge={
            <div className="rounded-2xl overflow-hidden bg-vermilion-600 text-white flex items-center justify-center p-6">
              <div className="text-center">
                <div className="font-display font-black text-4xl">₹{h.price_per_bed}</div>
                <div className="text-xs tracking-widest uppercase mt-1 opacity-80">Per bed / night</div>
              </div>
            </div>
          }
        />

        <div className="mt-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className={`text-[10px] tracking-widest uppercase font-bold px-2 py-1 rounded-full ${h.gender === "girls" ? "bg-pink-100 text-pink-700" : h.gender === "boys" ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700"}`}>{h.gender}</span>
              <span className="flex items-center gap-1 text-sm font-semibold"><Star size={14} className="fill-amber-600 text-amber-600" /> {h.rating} · {h.reviews} reviews</span>
            </div>
            <h1 data-testid="hostel-title" className="font-display font-black text-4xl sm:text-5xl tracking-tighter">{h.name}</h1>
            <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-slate-600 flex items-center gap-1"><MapPin size={14} /> {h.city}{h.coaching_centers?.[0] ? ` · near ${h.coaching_centers[0]}` : ""} <span className="text-xs text-slate-400 ml-1">· exact address unlocked after booking</span></div>
              <div className="flex items-center gap-2">
                <Button data-testid="hostel-share-btn" variant="outline" size="sm" onClick={() => setShareOpen(true)} className="rounded-full gap-2">
                  <Share2 size={14} /> Share
                </Button>
              </div>
            </div>

            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Beds available</div>
                <div className="mt-2 font-display font-black text-2xl">{h.available_beds}<span className="text-slate-400 text-sm font-normal">/{h.total_beds}</span></div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Coaching centers</div>
                <div className="mt-2 text-sm font-semibold">{(h.coaching_centers || []).join(", ") || "—"}</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Contact</div>
                <div className="mt-2 text-sm font-semibold flex items-center gap-1 text-slate-600">
                  <Phone size={12} /> Shared after booking
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">Owner phone + exact address unlocked on payment</div>
              </div>
            </div>

            <div className="mt-10">
              <h2 className="font-display font-bold text-2xl">About this hostel</h2>
              <p className="mt-3 text-slate-600 leading-relaxed">{h.description}</p>
            </div>

            <div className="mt-10">
              <HostelMapView
                lat={h.map_lat}
                lng={h.map_lng}
                address={h.map_address}
                name={h.name}
              />
            </div>

            <NearbyPlaces hostelId={h.id} />

            <div className="mt-10">
              <h2 className="font-display font-bold text-2xl">Amenities</h2>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(h.amenities || []).map((a) => {
                  const Icon = AMENITY_ICONS[a] || CheckCircle2;
                  return (
                    <div key={a} className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3">
                      <Icon size={18} className="text-orange-800" />
                      <span className="text-sm font-semibold">{a}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-10">
              <div className="flex items-baseline justify-between">
                <h2 className="font-display font-bold text-2xl">Student reviews</h2>
                <div className="text-sm text-slate-500 flex items-center gap-1">
                  <Star size={14} className="fill-amber-600 text-amber-600" />
                  <span className="font-bold text-slate-900">{h.rating || "—"}</span>
                  <span>· {reviews.length} review{reviews.length === 1 ? "" : "s"}</span>
                </div>
              </div>
              <div className="mt-4 space-y-3">
                {reviews.length === 0 && (
                  <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center text-slate-500 text-sm">
                    No reviews yet — be the first after your stay.
                  </div>
                )}
                {reviews.map((r) => (
                  <div key={r.id} data-testid={`review-${r.id}`} className="bg-white border border-slate-200 rounded-2xl p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-800 grid place-items-center font-bold">
                          {r.user_name?.[0]?.toUpperCase() || "S"}
                        </div>
                        <div>
                          <div className="font-semibold">{r.user_name}</div>
                          <div className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</div>
                        </div>
                      </div>
                      <div className="flex gap-0.5">
                        {[1,2,3,4,5].map((n) => (
                          <Star key={n} size={14} className={n <= r.rating ? "fill-amber-600 text-amber-600" : "text-slate-200"} />
                        ))}
                      </div>
                    </div>
                    {r.comment && <p className="mt-3 text-slate-700 text-sm leading-relaxed">{r.comment}</p>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Booking card */}
          <aside className="lg:col-span-4">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 sticky top-24">
              <div className="flex items-baseline gap-1 font-display">
                <IndianRupee size={20} strokeWidth={3} className="text-orange-800" />
                <span className="font-black text-4xl">{h.price_per_bed}</span>
                <span className="text-sm font-body text-slate-500">/ bed / night</span>
              </div>
              <div data-testid="monthly-saver-teaser" className="mt-3 flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
                <span className="text-emerald-600 text-lg leading-none">✨</span>
                <div className="text-xs text-emerald-900 leading-snug">
                  <b>Monthly saver:</b> Book <b>30+ nights</b> and get <b>10% off room rent</b> — auto-applied at checkout.
                </div>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <button data-testid="check-in-btn" className="text-left border border-slate-200 rounded-xl px-3 py-2 hover:border-orange-600 transition-colors">
                      <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500 flex items-center gap-1"><CalendarDays size={10} /> Check-in</div>
                      <div className="text-sm font-semibold">{format(checkIn, "d MMM yyyy")}</div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0"><Calendar mode="single" selected={checkIn} onSelect={(d) => d && setCheckIn(d)} disabled={(d) => d < new Date(Date.now() - 86400000)} /></PopoverContent>
                </Popover>
                <Popover>
                  <PopoverTrigger asChild>
                    <button data-testid="check-out-btn" className="text-left border border-slate-200 rounded-xl px-3 py-2 hover:border-orange-600 transition-colors">
                      <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500 flex items-center gap-1"><CalendarDays size={10} /> Check-out</div>
                      <div className="text-sm font-semibold">{format(checkOut, "d MMM yyyy")}</div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0"><Calendar mode="single" selected={checkOut} onSelect={(d) => d && setCheckOut(d)} disabled={(d) => d <= checkIn} /></PopoverContent>
                </Popover>
              </div>
              <div className="mt-3">
                <Label className="text-[10px] tracking-widest uppercase font-bold text-slate-500">Beds</Label>
                <Input data-testid="beds-input" type="number" min={1} max={5} value={beds} onChange={(e) => setBeds(Math.max(1, Math.min(5, +e.target.value || 1)))} className="mt-1" />
              </div>

              {(h.room_types || []).length > 0 && (
                <div className="mt-4" data-testid="room-types-picker">
                  <Label className="text-[10px] tracking-widest uppercase font-bold text-slate-500">Select room type</Label>
                  <div className="mt-2 grid grid-cols-1 gap-2">
                    {(h.room_types || []).map((rt) => {
                      const isPicked = selectedRoomTypeId === rt.id;
                      const soldOut = Number(rt.rooms_available || 0) < 1;
                      return (
                        <button
                          key={rt.id}
                          type="button"
                          data-testid={`room-type-option-${rt.id}`}
                          disabled={soldOut}
                          onClick={() => setSelectedRoomTypeId(isPicked ? null : rt.id)}
                          className={`text-left border rounded-xl px-3 py-2.5 transition-colors ${isPicked ? "border-orange-800 bg-orange-50" : "border-slate-200 hover:border-orange-300"} ${soldOut ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-bold truncate">{rt.name}</div>
                              <div className="text-[11px] text-slate-500">
                                {soldOut ? <span className="text-rose-600 font-semibold">Fully booked</span> : <><b className="text-emerald-700">{rt.rooms_available}</b> of {rt.rooms_total} rooms available</>}
                                {rt.beds_per_room > 1 && <> · {rt.beds_per_room} beds/room</>}
                                {rt.food_included && <> · <span className="text-emerald-700 font-bold">🍽 Food included</span></>}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-black text-orange-800">₹{Number(rt.price_per_month).toLocaleString("en-IN")}</div>
                              <div className="text-[10px] text-slate-500">/ month</div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {selectedRoomType && (
                    <div className="mt-2 text-[11px] text-slate-600 bg-marigold-50 border border-marigold-200 rounded-lg px-2.5 py-1.5" data-testid="room-type-billing-note">
                      {monthsCharged
                        ? <>Billed as <b>₹{Number(selectedRoomType.price_per_month).toLocaleString("en-IN")}</b> × <b>{monthsCharged} month{monthsCharged === 1 ? "" : "s"}</b> = <b>₹{hostelSubtotal.toLocaleString("en-IN")}</b></>
                        : <>Short stay — billed <b>₹{rtNightly.toLocaleString("en-IN")}/night</b> × <b>{nights} night{nights === 1 ? "" : "s"}</b> = <b>₹{hostelSubtotal.toLocaleString("en-IN")}</b></>}
                    </div>
                  )}
                </div>
              )}

              {h.ac_available && (
                <div className="mt-4">
                  <Label className="text-[10px] tracking-widest uppercase font-bold text-slate-500">Room type</Label>
                  <RadioGroup value={roomType} onValueChange={setRoomType} className="mt-2 grid grid-cols-2 gap-2">
                    <label data-testid="room-type-nonac" className={`border rounded-xl px-3 py-2.5 cursor-pointer transition-colors ${roomType === "Non-AC" ? "border-orange-800 bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="Non-AC" className="border-orange-800" />
                        <div>
                          <div className="text-xs font-bold">Non-A/C</div>
                          <div className="text-[11px] text-slate-500">₹{h.price_per_bed}/night</div>
                        </div>
                      </div>
                    </label>
                    <label data-testid="room-type-ac" className={`border rounded-xl px-3 py-2.5 cursor-pointer transition-colors ${roomType === "AC" ? "border-orange-800 bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="AC" className="border-orange-800" />
                        <div>
                          <div className="text-xs font-bold flex items-center gap-1"><Snowflake size={10} className="text-sky-600" /> A/C</div>
                          <div className="text-[11px] text-slate-500">₹{h.ac_price_per_bed}/night</div>
                        </div>
                      </div>
                    </label>
                  </RadioGroup>
                </div>
              )}

              {foodInRent && (
                <div data-testid="food-included-note" className="mt-4 flex items-start gap-3 border border-emerald-300 bg-emerald-50 rounded-xl px-3 py-3">
                  <Utensils size={15} className="text-emerald-700 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-sm font-bold text-emerald-800">Food included in rent</div>
                    <div className="text-[11px] text-emerald-700 mt-0.5">Monthly meals are already covered in this room type's rent — no extra food charges.</div>
                  </div>
                </div>
              )}

              {h.meal_available && !foodInRent && (
                <label data-testid="meal-toggle" className={`mt-4 flex items-start gap-3 cursor-pointer border rounded-xl px-3 py-3 transition-colors ${includeMeal ? "border-amber-600 bg-amber-50/60" : "border-slate-200 hover:border-slate-300"}`}>
                  <Checkbox checked={includeMeal} onCheckedChange={(v) => setIncludeMeal(!!v)} className="mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-bold flex items-center gap-1"><Utensils size={12} className="text-amber-600" /> Add meals</div>
                      <div className="text-sm font-bold text-amber-700">+₹{h.meal_price}/night</div>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{h.meal_description || "Home-cooked meals included"}</div>
                  </div>
                </label>
              )}

              {nights >= 365 && (
                <div data-testid="quarterly-plan-block" className="mt-4 border-2 border-turmeric-400 rounded-xl p-3 bg-turmeric-50">
                  <div className="text-[10px] tracking-widest uppercase font-black text-vermilion-700 mb-2">Yearly stay · payment option</div>
                  <label className={`flex items-start gap-3 cursor-pointer border rounded-lg px-3 py-2 mb-2 ${paymentPlan === "full" ? "border-vermilion-600 bg-white font-bold" : "border-slate-200 bg-white"}`}>
                    <input type="radio" name="pplan" data-testid="plan-full" checked={paymentPlan === "full"} onChange={() => setPaymentPlan("full")} className="mt-1 accent-vermilion-600" />
                    <div className="flex-1">
                      <div className="text-sm">Pay in full now</div>
                      <div className="text-[11px] text-slate-500">₹{total.toLocaleString("en-IN")} charged in one Razorpay transaction.</div>
                    </div>
                  </label>
                  <label className={`flex items-start gap-3 cursor-pointer border rounded-lg px-3 py-2 ${paymentPlan === "quarterly" ? "border-vermilion-600 bg-white font-bold" : "border-slate-200 bg-white"}`}>
                    <input type="radio" name="pplan" data-testid="plan-quarterly" checked={paymentPlan === "quarterly"} onChange={() => setPaymentPlan("quarterly")} className="mt-1 accent-vermilion-600" />
                    <div className="flex-1">
                      <div className="text-sm">Pay quarterly · 4 instalments</div>
                      <div className="text-[11px] text-slate-500">≈₹{Math.floor(total / 4).toLocaleString("en-IN")} now, plus 3 more due every 3 months. Bed is locked as soon as the 1st instalment is paid.</div>
                    </div>
                  </label>
                </div>
              )}

              <div className="mt-5 border-t border-slate-200 pt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">
                    {selectedRoomType
                      ? (monthsCharged
                          ? <>{selectedRoomType.name} · ₹{Number(selectedRoomType.price_per_month).toLocaleString("en-IN")} × {monthsCharged} mo</>
                          : <>{selectedRoomType.name} · ₹{rtNightly.toLocaleString("en-IN")} × {nights} night{nights === 1 ? "" : "s"}</>)
                      : <>{roomType === "AC" ? "A/C" : "Non-A/C"} · ₹{rate} × {nights} × {beds}</>}
                  </span>
                  <span className="font-semibold">₹{hostelSubtotal.toLocaleString("en-IN")}</span>
                </div>
                {mealCost > 0 && (
                  <div className="flex justify-between"><span className="text-slate-600">Meals · ₹{h.meal_price} × {nights} × {beds}</span><span className="font-semibold">₹{mealCost.toLocaleString("en-IN")}</span></div>
                )}
                {foodInRent && (
                  <div data-testid="food-included-line" className="flex justify-between text-emerald-700"><span>Meals · included in rent</span><span className="font-semibold">₹0</span></div>
                )}
                {platformFee > 0 && (
                  <div className="flex justify-between"><span className="text-slate-600">Platform fee (₹50 × {nights} × {beds})</span><span className="font-semibold">₹{platformFee.toLocaleString("en-IN")}</span></div>
                )}
                <div className="flex justify-between font-display font-black text-lg pt-2 border-t border-dashed border-slate-200"><span>Total</span><span>₹{total.toLocaleString("en-IN")}</span></div>
                <div data-testid="checkout-no-fees-badge" className="flex items-center gap-1.5 rounded-lg bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 text-[11px] font-bold text-emerald-800">
                  <ShieldCheck size={13} className="flex-shrink-0" /> No hidden charges — you pay exactly this amount
                </div>
              </div>

              {cannotBookReason && (
                <div data-testid="cannot-book-banner" className="mt-5 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2">
                  <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold">You can't book from this account</div>
                    <p className="mt-0.5">{cannotBookReason}</p>
                    {needsKyc && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button
                          data-testid="go-to-kyc-btn"
                          onClick={() => nav(`/kyc?next=/hostels/${id}`)}
                          className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-vermilion-600 hover:bg-vermilion-700 text-white font-semibold"
                        >
                          <ShieldCheck size={12} /> {user.kyc_status === "pending" ? "View KYC status" : "Verify Aadhaar now"}
                        </button>
                      </div>
                    )}
                    {isOwnerAccount && !isOwnHostel && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button
                          data-testid="switch-to-student-btn"
                          onClick={() => { logout?.(); nav(`/signup?role=student&next=/hostels/${id}`); }}
                          className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-orange-800 hover:bg-orange-900 text-white font-semibold"
                        >
                          <LogOut size={12} /> Sign out & create student account
                        </button>
                        <button
                          onClick={() => { logout?.(); nav(`/login?next=/hostels/${id}`); }}
                          className="inline-flex items-center gap-1 h-8 px-3 rounded-full border border-amber-300 text-amber-900 hover:bg-amber-100 font-semibold"
                        >
                          Sign in as student
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <Button
                data-testid="book-now-btn"
                onClick={handleBook}
                disabled={loading || !!cannotBookReason}
                className="btn-pop mt-5 w-full rounded-full bg-amber-600 hover:bg-amber-700 text-white h-12 font-semibold shadow-lg shadow-amber-600/25 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Creating…" : cannotBookReason ? "Booking blocked" : "Book now"}
              </Button>
              <Button
                data-testid="schedule-visit-btn"
                variant="outline"
                onClick={() => setVisitOpen(true)}
                className="btn-pop mt-2 w-full rounded-full border-orange-800 text-orange-900 hover:bg-orange-50 h-12 font-semibold gap-2"
              >
                <CalendarClock size={16} /> Schedule a free visit
              </Button>
              <div className="mt-3 text-[11px] text-slate-500 flex items-center gap-1 justify-center"><ShieldCheck size={12} className="text-orange-800" /> Aadhaar KYC required at check-in</div>
            </div>
          </aside>
        </div>
      </div>
      <ShareModal hostel={h} open={shareOpen} onClose={() => setShareOpen(false)} />
      <ScheduleVisitDialog hostel={h} open={visitOpen} onClose={() => setVisitOpen(false)} user={user} />
      <Footer />
    </div>
  );
}
